import React, { useEffect, useState, useCallback } from 'react';
import { Volume2, Volume1, VolumeX, Activity, Radio } from 'lucide-react';
import { getAudioEngine, AudioEngine, AudioEngineState } from '../../audio';
import { AudioMood } from '../../types/audio';

export interface AudioControlsProps {
  engine?: AudioEngine;
  compact?: boolean;
  showMoodBadge?: boolean;
  className?: string;
}

const MOOD_CONFIG: Record<
  AudioMood,
  { label: string; color: string; bg: string; borderColor: string; description: string }
> = {
  deep_drone: {
    label: 'DEEP DRONE // 40.0Hz',
    color: 'var(--planck-cyan, #00f3ff)',
    bg: 'rgba(0, 243, 255, 0.08)',
    borderColor: 'rgba(0, 243, 255, 0.3)',
    description: 'Binaural Theta Beat & Cosmic Hiss',
  },
  quantum_flutter: {
    label: 'QUANTUM FLUTTER',
    color: 'var(--seam-violet, #9d4edd)',
    bg: 'rgba(157, 78, 221, 0.08)',
    borderColor: 'rgba(157, 78, 221, 0.3)',
    description: 'Detuned Overtones & Void Static',
  },
  subharmonic_pulse: {
    label: 'SUBHARMONIC PULSE // 20Hz',
    color: 'var(--decay-amber, #ffb700)',
    bg: 'rgba(255, 183, 0, 0.08)',
    borderColor: 'rgba(255, 183, 0, 0.3)',
    description: 'Jump Drive Core Throbbing',
  },
  caesura_silence: {
    label: 'CAESURA SILENCE // 0Hz',
    color: 'var(--shear-crimson, #ff2a4b)',
    bg: 'rgba(255, 42, 75, 0.08)',
    borderColor: 'rgba(255, 42, 75, 0.3)',
    description: 'Unrendered Planck Gap Stillness',
  },
  synaptic_alarm: {
    label: 'SYNAPTIC ALARM',
    color: 'var(--shear-crimson, #ff2a4b)',
    bg: 'rgba(255, 42, 75, 0.12)',
    borderColor: 'rgba(255, 42, 75, 0.4)',
    description: 'Neurological Collapse Tremors',
  },
};

export const AudioControls: React.FC<AudioControlsProps> = ({
  engine: propEngine,
  compact = false,
  showMoodBadge = true,
  className = '',
}) => {
  const [engineInstance] = useState<AudioEngine>(() => propEngine || getAudioEngine());
  const [state, setState] = useState<AudioEngineState>(() => engineInstance.getState());

  useEffect(() => {
    const unsubscribe = engineInstance.onStateChange((newState) => {
      setState(newState);
    });
    return unsubscribe;
  }, [engineInstance]);

  const handleToggleMute = useCallback(() => {
    if (!state.isUnlocked) {
      engineInstance.unlockAudio();
    } else {
      engineInstance.toggleMute();
    }
  }, [engineInstance, state.isUnlocked]);

  const handleVolumeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = parseFloat(e.target.value) / 100;
      engineInstance.setMasterVolume(val);
      if (state.isMuted) {
        engineInstance.toggleMute();
      }
    },
    [engineInstance, state.isMuted]
  );

  const moodInfo = MOOD_CONFIG[state.currentCue.mood] || MOOD_CONFIG.deep_drone;
  const volumePercent = Math.round(state.volume * 100);

  return (
    <div
      className={`caesura-audio-controls ${className}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: compact ? '0.5rem' : '0.85rem',
        padding: compact ? '0.25rem 0.5rem' : '0.4rem 0.75rem',
        backgroundColor: 'var(--void-surface, #0c121c)',
        border: '1px solid var(--void-border, #1c2738)',
        borderRadius: '3px',
        color: 'var(--text-prose, #d8e2ec)',
        fontFamily: 'var(--font-mono, monospace)',
        fontSize: '0.75rem',
      }}
    >
      {/* Mute / Unmute Button */}
      <button
        onClick={handleToggleMute}
        className="caesura-audio-mute-btn"
        aria-label={state.isMuted ? 'Unmute audio' : 'Mute audio'}
        title={
          !state.isUnlocked
            ? 'Audio suspended: Click to unlock'
            : state.isMuted
            ? 'Unmute audio'
            : 'Mute audio'
        }
        style={{
          background: 'transparent',
          border: 'none',
          color: !state.isUnlocked
            ? 'var(--text-muted, #6b7c96)'
            : state.isMuted
            ? 'var(--shear-crimson, #ff2a4b)'
            : 'var(--planck-cyan, #00f3ff)',
          cursor: 'pointer',
          padding: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'color 0.15s ease',
        }}
      >
        {!state.isUnlocked || state.isMuted ? (
          <VolumeX size={16} />
        ) : state.volume < 0.4 ? (
          <Volume1 size={16} />
        ) : (
          <Volume2 size={16} />
        )}
      </button>

      {/* Volume Slider & Percent Readout */}
      <div
        className="caesura-audio-slider-wrap"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.4rem',
        }}
      >
        <input
          type="range"
          min="0"
          max="100"
          value={state.isMuted ? 0 : volumePercent}
          onChange={handleVolumeChange}
          aria-label="Master volume"
          title={`Volume: ${volumePercent}%`}
          style={{
            width: compact ? '60px' : '84px',
            height: '4px',
            accentColor: 'var(--planck-cyan, #00f3ff)',
            cursor: 'pointer',
          }}
        />
        <span
          style={{
            fontSize: '0.7rem',
            color: 'var(--text-muted, #6b7c96)',
            minWidth: '28px',
            textAlign: 'right',
          }}
        >
          {state.isMuted ? '0%' : `${volumePercent}%`}
        </span>
      </div>

      {/* Active Mood Badge */}
      {showMoodBadge && (
        <div
          className="caesura-audio-mood-badge"
          title={moodInfo.description}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.35rem',
            padding: '0.2rem 0.5rem',
            backgroundColor: moodInfo.bg,
            border: `1px solid ${moodInfo.borderColor}`,
            borderRadius: '2px',
            color: moodInfo.color,
            fontSize: '0.65rem',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            fontWeight: 600,
          }}
        >
          {state.currentCue.mood === 'caesura_silence' ? (
            <Radio size={12} style={{ opacity: 0.6 }} />
          ) : (
            <Activity size={12} className="caesura-audio-pulse-icon" />
          )}
          <span>{moodInfo.label}</span>
        </div>
      )}
    </div>
  );
};

export default AudioControls;
